/**
 * Modal Component
 * Overlay modal for displaying information
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { designTokens, borderRadius } from '../../constants/designTokens';
import { useLanguage } from '../../i18n';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

export function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const { t } = useLanguage();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title-wrapper">
              <svg 
                className="modal-icon"
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" fill={designTokens.colors.brand.primary}/>
                <path 
                  d="M12 16v-4m0-4h.01M12 8h.01" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="8" r="0.5" fill="white"/>
              </svg>
              <h2 className="modal-title">{title}</h2>
            </div>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label={t('common.closeModal')}
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M18 6L6 18M6 6l12 12" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="modal-body">
            {children}
          </div>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: ${designTokens.spacing.lg};
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background-color: ${designTokens.colors.background.secondary};
          border-radius: ${borderRadius.lg};
          max-width: 520px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${designTokens.spacing.lg};
          border-bottom: 1px solid ${designTokens.colors.border.light};
        }

        .modal-title-wrapper {
          display: flex;
          align-items: center;
          gap: ${designTokens.spacing.md};
        }

        .modal-icon {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
        }

        .modal-title {
          font-size: ${designTokens.typography.fontSize.xl};
          font-weight: ${designTokens.typography.fontWeight.semibold};
          color: ${designTokens.colors.text.primary};
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          padding: ${designTokens.spacing.sm};
          cursor: pointer;
          color: ${designTokens.colors.text.secondary};
          transition: color 0.2s ease;
          border-radius: ${borderRadius.md};
        }

        .modal-close:hover {
          color: ${designTokens.colors.text.primary};
          background-color: ${designTokens.colors.background.tertiary};
        }

        .modal-body {
          padding: ${designTokens.spacing.lg};
          overflow-y: auto;
          max-height: calc(90vh - 100px);
        }

        /* Mobile optimizations */
        ${designTokens.media.mobile} {
          .modal-overlay {
            padding: ${designTokens.spacing.md};
          }

          .modal-content {
            max-width: 100%;
          }

          .modal-header {
            padding: ${designTokens.spacing.md};
          }

          .modal-icon {
            width: 28px;
            height: 28px;
          }

          .modal-title {
            font-size: ${designTokens.typography.fontSize.lg};
          }

          .modal-body {
            padding: ${designTokens.spacing.md};
          }
        }
      `}</style>
    </>
  );
}
